import { FC } from 'react';
import { useQuery } from 'react-query';
import { getOrgPolicyV1, patchPolicyV1 } from '@/lib/services/policy-v1.service';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import FormSection from '@/components/form/form-section';
import { Grid } from '@mui/material';
import useQueryHelper from '@/hooks/use-query-helper';
import InputControl from '@/components/form/input-control';
import useFormHandle from '@/hooks/use-form-handle.hook';
import FormButtons from '@/components/form/form-buttons';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { FieldError } from '@/lib/services';
import { McpBlockActionOptions } from '@/lib/models/policy-model-v1/mcp-controls';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['mcpControls'];
const formModel = new PolicyModelV1();

const OrgMcpAccessForm: FC = () => {
  const { toast, errorToast } = useToast();
  const { canChangePolicy } = useFeature();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const { patched, error, fieldErrors } = await patchPolicyV1(
        values.id,
        values.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const query = useQuery([QueryKey.OrgPolicyView], async () => getOrgPolicyV1());
  const { showLoader } = useQueryHelper(query);

  if (showLoader) {
    return;
  }

  return (
    <Form formHandle={formHandle} query={query}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      <FormSection title="MCP Tool Block List" scrollId="blocked-mcp-tools">
        <FormSectionSubtitle>
          Use the table below to explicitly block individual MCP tools for this organization.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              name="mcpControls"
              label="Block List"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="mcp-tools-acl-v1"
              aclVariant="block"
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="MCP Tool Allow List" scrollId="allowed-mcp-tools">
        <FormSectionSubtitle>
          Use the table below to explicitly allow individual MCP tools for this organization.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              name="mcpControls"
              label="Allow List"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="mcp-tools-acl-v1"
              aclVariant="allow"
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Allow Read-only">
        <FormSectionSubtitle>
          When this setting is enabled, any read-only MCP tool not explicitly allowed or blocked
          above will be allowed, and all other tools will have the Catch-all action applied.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              label="Allow Read-only"
              name="mcpControls.action"
              type="mcp-tool-action-switch-v1"
              formHandle={formHandle}
              readonly={!canChangePolicy}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Catch-all Action">
        <FormSectionSubtitle>
          Choose what action is taken for any MCP tool that is not explicitly allowed or blocked by
          the above settings.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              label="Catch-all Action"
              name="mcpControls.blockAction"
              type="select"
              options={McpBlockActionOptions}
              formHandle={formHandle}
              readonly={!canChangePolicy}
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default OrgMcpAccessForm;
